import { useEffect, useRef, useState, type FormEvent } from 'react'
import { AlertTriangle, Loader2, Mail, ShieldAlert, X } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import { ROLE_LABELS, type ManagedUser } from '../types/identity'

/**
 * In-page dialog for changing somebody's sign-in address.
 *
 * Deliberately not a `window.prompt`: this is an identity change that ends the
 * person's sessions everywhere, so the consequences are spelled out (D-30).
 */
export function EmailChangeDialog({
  target,
  onClose,
  onSaved,
}: {
  target: ManagedUser
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [value, setValue] = useState(target.email)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [violations, setViolations] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const unchanged = value.trim().toLowerCase() === target.email.toLowerCase()

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (unchanged) {
      onClose()
      return
    }
    setBusy(true)
    setError(null)
    setViolations([])
    void (async () => {
      try {
        const updated = await api.changeUserEmail(target.id, value.trim())
        onSaved(
          `${target.full_name}'s sign-in address is now ${updated.email} — their sessions were ended and the address must be confirmed.`,
        )
        onClose()
      } catch (caught) {
        if (caught instanceof ApiError) {
          setError(caught.message)
          setViolations(caught.violations)
        } else {
          setError('That change could not be saved')
        }
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Change the sign-in address for ${target.full_name}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form
        onSubmit={submit}
        className="max-h-[92dvh] w-full max-w-lg space-y-4 overflow-y-auto rounded-t-3xl border border-slate-200 bg-white p-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl sm:max-h-[85vh] sm:rounded-2xl sm:p-5 dark:border-white/10 dark:bg-ink-900"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-salsa-500/10 text-salsa-600 dark:text-salsa-300">
            <Mail className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="leading-tight">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Change sign-in address
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {target.full_name} · {ROLE_LABELS[target.role] ?? target.role}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="icon-button ml-auto"
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="new-sign-in-address"
            className="block text-xs font-medium text-slate-600 dark:text-slate-300"
          >
            New e-mail address
          </label>
          <input
            id="new-sign-in-address"
            ref={inputRef}
            type="email"
            autoComplete="off"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5"
            required
          />
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Currently {target.email}</p>
        </div>

        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            Signing in is tied to this address. Saving ends <strong>every session</strong> they have
            open, and the new address must be confirmed before password resets can reach it. Their
            authenticator app keeps working.
          </span>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <div className="space-y-1">
              <p>{error}</p>
              {violations.length > 0 && (
                <ul className="list-disc space-y-0.5 pl-4">
                  {violations.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4 dark:border-white/5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || unchanged}
            className="inline-flex items-center gap-2 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-salsa-600 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save new address
          </button>
        </div>
      </form>
    </div>
  )
}
